import { useState } from "react";
import { CheckCircle2, X, Clock, Wallet, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { fmtPrice, withWatermark } from "@/lib/utils";

const hh = (h) => `${String(h).padStart(2, "0")}h`;

export const DayUseBookingModal = ({ hotel, slot, onClose, onBooked, createReservation }) => {
  const slots = hotel.creneaux || hotel.windows || [];
  const [sel, setSel] = useState(slot || slots[0] || null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const sameDuree = slots.filter((c) => c.duree === (sel?.duree ?? hotel.duree));
  const roomImage = hotel.images?.[1] || hotel.image;

  const submit = async () => {
    if (!sel) return toast.error("Choisissez un creneau");
    if (name.trim().length < 2) return toast.error("Indiquez votre nom");
    if (phone.replace(/\D/g, "").length < 8) return toast.error("Numero invalide");
    setBusy(true);
    try {
      const r = await createReservation(hotel.id, { name: name.trim(), phone: phone.trim(), date: hotel.date, hour: sel.hour, duree: sel.duree ?? hotel.duree, rooms: 1 });
      setDone(r);
      onBooked?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Reservation impossible"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="day-use-modal">
      <div className="bg-white rounded-3xl w-full max-w-lg overflow-hidden relative shadow-2xl">
        <button onClick={onClose} className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-white/90 shadow flex items-center justify-center text-[var(--ink)]" data-testid="day-use-modal-close"><X size={18} /></button>
        {done ? (
          <div className="text-center p-8" data-testid="day-use-done">
            <CheckCircle2 size={48} className="mx-auto text-emerald-600" />
            <h3 className="font-display font-extrabold text-2xl mt-3">Reservation confirmee !</h3>
            <p className="text-sm mt-2">Ref <b>{done.reservation.ref}</b> · {hotel.name} le {hotel.date} de {hh(sel.hour)} a {hh(sel.end)}.</p>
            <p className="text-sm mt-1">A payer sur place : <b>{fmtPrice(done.reservation.total_price)}</b>. Un SMS de confirmation vous a ete envoye.</p>
            <a href={done.link} className="btn-primary mt-5 inline-flex" data-testid="day-use-done-link">Suivre ma reservation</a>
          </div>
        ) : (
          <>
            <div className="relative h-40">
              <img src={withWatermark(roomImage)} alt={hotel.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-3 left-4 right-4 text-white">
                <div className="font-display font-extrabold text-xl leading-tight">{hotel.name}</div>
                <div className="text-xs text-white/85">{hotel.quartier ? `${hotel.quartier}, ` : ""}{hotel.city} · {hotel.date} · -{hotel.offre.reduction}%</div>
              </div>
            </div>
            <div className="p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-[var(--ink-soft)]">Votre creneau {sel ? `· ${sel.duree ?? hotel.duree} h` : ""}</div>
              <div className="flex flex-wrap gap-2 mt-2 max-h-28 overflow-y-auto" data-testid="day-use-modal-slots">
                {sameDuree.map((c) => (
                  <button key={`${c.hour}-${c.duree}`} type="button" onClick={() => setSel(c)}
                    className={`rounded-xl border-2 px-3 py-1.5 text-sm font-bold transition-colors ${sel?.hour === c.hour && sel?.duree === c.duree ? "border-orange-500 bg-orange-500 text-white" : "border-neutral-200 hover:border-orange-400"}`}
                    data-testid={`day-use-slot-${c.hour}`}>
                    {hh(c.hour)} - {hh(c.end)}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <input placeholder="Votre nom" value={name} onChange={(e) => setName(e.target.value)} className="!mb-0" data-testid="day-use-name" />
                <input placeholder="Telephone (SMS)" value={phone} onChange={(e) => setPhone(e.target.value)} className="!mb-0" data-testid="day-use-phone" />
              </div>
              {sel && (
                <div className="mt-4 rounded-2xl bg-[#faf8f4] border border-neutral-200 p-4 flex items-center justify-between">
                  <div className="text-sm">
                    <div className="font-bold inline-flex items-center gap-1.5"><Clock size={14} /> {hh(sel.hour)} - {hh(sel.end)} · {sel.duree ?? hotel.duree} h</div>
                    <div className="text-xs text-[var(--ink-soft)] mt-0.5 inline-flex items-center gap-3"><span className="inline-flex items-center gap-1"><Wallet size={11} /> Paiement sur place</span><span className="inline-flex items-center gap-1"><ShieldCheck size={11} /> Annulation gratuite</span></div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-extrabold text-orange-600 whitespace-nowrap" data-testid="day-use-total">{fmtPrice(sel.price)}</div>
                    {sel.prix_normal && <div className="text-xs line-through text-[var(--ink-soft)]">{fmtPrice(sel.prix_normal)}</div>}
                  </div>
                </div>
              )}
              <button onClick={submit} disabled={busy || !sel} className="btn-primary w-full mt-4 !py-3" data-testid="day-use-submit">{busy ? "Confirmation..." : "Confirmer sans carte bancaire"}</button>
              <p className="text-[11px] text-[var(--ink-soft)] mt-2 text-center">Aucun prepaiement. Annulation gratuite jusqu&apos;au debut du creneau.</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
